/**
 * Safe local and tenant-aware date utilities
 */

export const TIMEZONE_OPTIONS = [
  { value: 'Asia/Dubai', label: 'Asia/Dubai (UTC+04:00 - UAE, Gulf Standard Time)' },
  { value: 'Asia/Riyadh', label: 'Asia/Riyadh (UTC+03:00 - Saudi Arabia)' },
  { value: 'Asia/Qatar', label: 'Asia/Qatar (UTC+03:00 - Qatar)' },
  { value: 'Asia/Kuwait', label: 'Asia/Kuwait (UTC+03:00 - Kuwait)' },
  { value: 'Asia/Bahrain', label: 'Asia/Bahrain (UTC+03:00 - Bahrain)' },
  { value: 'Asia/Muscat', label: 'Asia/Muscat (UTC+04:00 - Oman)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (UTC+05:30 - India, Sri Lanka)' },
  { value: 'Asia/Karachi', label: 'Asia/Karachi (UTC+05:00 - Pakistan)' },
  { value: 'Asia/Dhaka', label: 'Asia/Dhaka (UTC+06:00 - Bangladesh)' },
  { value: 'Asia/Kathmandu', label: 'Asia/Kathmandu (UTC+05:45 - Nepal)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (UTC+08:00 - Singapore, Malaysia)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong Kong (UTC+08:00 - Hong Kong, China)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+09:00 - Japan)' },
  { value: 'Europe/London', label: 'Europe/London (UTC+00:00 / +01:00 - UK, Ireland)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (UTC+01:00 / +02:00 - France, Germany, Europe)' },
  { value: 'Africa/Cairo', label: 'Africa/Cairo (UTC+02:00 / +03:00 - Egypt)' },
  { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (UTC+02:00 - South Africa)' },
  { value: 'Africa/Nairobi', label: 'Africa/Nairobi (UTC+03:00 - East Africa)' },
  { value: 'Africa/Lagos', label: 'Africa/Lagos (UTC+01:00 - West Africa)' },
  { value: 'America/New_York', label: 'America/New York (UTC-05:00 / -04:00 - US Eastern)' },
  { value: 'America/Chicago', label: 'America/Chicago (UTC-06:00 / -05:00 - US Central)' },
  { value: 'America/Denver', label: 'America/Denver (UTC-07:00 / -06:00 - US Mountain)' },
  { value: 'America/Los_Angeles', label: 'America/Los Angeles (UTC-08:00 / -07:00 - US Pacific)' },
  { value: 'America/Toronto', label: 'America/Toronto (UTC-05:00 - Canada Eastern)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (UTC+10:00 / +11:00 - Sydney, Melbourne)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (UTC+12:00 / +13:00 - New Zealand)' },
  { value: 'UTC', label: 'UTC (UTC+00:00 - Universal Coordinated Time)' },
];

/**
 * Returns a YYYY-MM-DD string representation of a Date in the local browser / device time.
 * Avoids the UTC-shift bug caused by `d.toISOString().split('T')[0]`.
 *
 * @param {Date|string|number} [dateInput=new Date()]
 * @returns {string} YYYY-MM-DD
 */
export function getLocalDateString(dateInput = new Date()) {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format date for display based on company's date format pattern.
 *
 * @param {Date|string|number} dateInput
 * @param {string} [formatPattern='dd/MM/yyyy']
 * @returns {string}
 */
export function formatDisplayDate(dateInput, formatPattern = 'dd/MM/yyyy') {
  if (!dateInput) return '-';
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);

  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();

  switch (formatPattern) {
    case 'MM/dd/yyyy':
      return `${month}/${day}/${year}`;
    case 'yyyy-MM-dd':
      return `${year}-${month}-${day}`;
    case 'dd-MM-yyyy':
      return `${day}-${month}-${year}`;
    case 'dd/MM/yyyy':
    default:
      return `${day}/${month}/${year}`;
  }
}

/**
 * Format date and time for display based on company's date format pattern and timezone.
 *
 * @param {Date|string|number} dateInput
 * @param {string} [formatPattern='dd/MM/yyyy']
 * @param {string} [timezone] e.g. 'Asia/Dubai'
 * @returns {string} e.g. "19/09/2026, 06:37:15 PM"
 */
export function formatDisplayDateTime(dateInput, formatPattern = 'dd/MM/yyyy', timezone) {
  if (!dateInput) return '-';
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);

  const tz = timezone || 'Asia/Dubai';

  try {
    const options = {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(d);
    const map = {};
    parts.forEach((p) => { map[p.type] = p.value; });

    const day = map.day || String(d.getDate()).padStart(2, '0');
    const month = map.month || String(d.getMonth() + 1).padStart(2, '0');
    const year = map.year || d.getFullYear();
    const hour = map.hour || '12';
    const minute = map.minute || '00';
    const second = map.second || '00';
    const dayPeriod = map.dayPeriod ? map.dayPeriod.toUpperCase() : '';

    let formattedDate = `${day}/${month}/${year}`;
    if (formatPattern === 'MM/dd/yyyy') formattedDate = `${month}/${day}/${year}`;
    else if (formatPattern === 'yyyy-MM-dd') formattedDate = `${year}-${month}-${day}`;
    else if (formatPattern === 'dd-MM-yyyy') formattedDate = `${day}-${month}-${year}`;

    const timeStr = dayPeriod ? `${hour}:${minute}:${second} ${dayPeriod}` : `${hour}:${minute}:${second}`;
    return `${formattedDate}, ${timeStr}`;
  } catch (err) {
    return d.toLocaleString([], { timeZone: tz });
  }
}

